import axios from 'axios'
import _ from 'lodash'
import { connectToChild } from 'penpal'
/* eslint-disable node/no-deprecated-api */
import { resolve } from 'url'

export const PluginSlotMixin = {
  props: {
    pluginSlotName: {
      type: String,
      required: true
    },
    integrationsUrl: {
      type: String
    },
    context: {
      type: Object,
      default: () => ({})
    },
    plugin: {
      type: Object,
      default: () => ({})
    },
    pluginSlotDisabled: {
      type: Boolean,
      default: false
    },
    disabledMessage: {
      type: String,
      default: ''
    },
    accessToken: {
      type: String,
      default: ''
    },
    pluginMethods: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      componentLoadingIndicator: false,
      fullscreenLoadingIndicator: false,
      handleResponseName: '',
      handleErrorName: '',
      // Define updatePluginIframeContext method in the data so that each component has a unique
      // instance of this method. See https://stackoverflow.com/q/45908853/875048.
      updatePluginIframeContext: _.debounce(async function (val) {
        if (this.pluginIframeConnection) {
          const info = this.getSlotInfo()
          const iframe = await this.pluginIframeConnection.promise
          iframe.contextUpdate(info.handler, val)
        }
      })
    }
  },
  created () {
    // This attribute should not be reactive to avoid issues when setting a
    // cross-domain object on it.
    this.pluginIframeConnection = null
  },
  computed: {
    pluginSlot () {
      return this.plugin.plugin_slots.find(slot => {
        return slot.slot_name === this.pluginSlotName
      })
    },
    combinedMethods () {
      return {
        initializeSlot: this.initializeSlot,
        makeRequest: this.makeRequest,
        getSlotInfo: this.getSlotInfo,
        displayErrors: this.displayErrors,
        displayMessage: this.displayMessage,
        displayInfoPopUp: this.displayInfoPopUp,
        getAccessToken: this.getAccessToken,
        ...this.getSlotExposedMethods(),
        ...this.pluginMethods
      }
    }
  },
  watch: {
    context: {
      handler: function (val, prev) {
        this.updatePluginIframeContext(val)
      },
      deep: true
    }
  },
  beforeDestroy: async function () {
    await this.runPendingExecutions()
    this.iframe.parentElement.removeChild(this.iframe)
    if (this.pluginIframeConnection) {
      this.pluginIframeConnection.destroy()
      this.pluginIframeConnection = null
    }
  },
  methods: {
    async request (slotIndex, request, responseHandler, errorHandler) {
      const { type } = request
      const isBriteCoreRequest = type === 'integration'
      const url = `${isBriteCoreRequest ? this.integrationsUrl : '/api'}${request.url}`
      if (url && isBriteCoreRequest) {
        // restrict callable urls to those that look like https://integrations.*.britecore.com/*
        const urlParts = url
          .replace('http://', '')
          .replace('https://', '')
          .split('/')

        if (urlParts[0] !== this.integrationsUrl.replace(/https?:\/\//, '')) {
          errorHandler(slotIndex, 'Endpoint not permitted')
          return
        }
      }

      try {
        const response = await axios({
          ...request,
          ...request.config,
          url: url,
          headers: { Authorization: this.accessToken, ...request.headers }
        })
        responseHandler(slotIndex, response)
      } catch (error) {
        errorHandler(slotIndex, JSON.stringify(error.response))
      }
    },
    handleResponse (slotIndex, { data, headers, status, statusText }) {
      this.pluginIframeConnection.promise.then(iframe => {
        const handler = iframe[this.handleResponseName]
        return handler({
          slotIndex,
          response: { data, headers, status, statusText }
        })
      })
    },
    handleError (slotIndex, error) {
      this.pluginIframeConnection.promise.then(iframe => {
        const handler = iframe[this.handleErrorName]
        return handler({ slotIndex, error })
      })
    },
    makeRequest ({ request, slotIndex }) {
      this.request(slotIndex, request, this.handleResponse, this.handleError)
    },
    initializeSlot () {
      // no-op
    },
    getSlotInfo () {
      const splittedName = this.pluginSlotName.split(':')
      return {
        name: this.pluginSlotName,
        context: this.context,
        plugin: this.plugin,
        handler: splittedName[splittedName.length - 1]
      }
    },
    createPluginIframe (src, containerElement, classList) {
      const iframe = document.createElement('iframe')
      for (const cls of classList) {
        iframe.classList.add(cls)
      }
      iframe.src = src
      containerElement.appendChild(iframe)
      this.iframe = iframe
      return iframe
    },
    showLoadingIndicator (type = 'component', boolean = true) {
      if (type === 'component') this.componentLoadingIndicator = boolean
      if (type === 'fullscreen') this.fullscreenLoadingIndicator = boolean
    },
    async connectToPluginIframe () {
      const baseUrl = this.plugin.base_url ? this.plugin.base_url : this.integrationsUrl
      const url = resolve(baseUrl, this.pluginSlot.plugin_file_path)
      const containerElement = document.body
      const iframeEl = this.createPluginIframe(url, containerElement, ['hide'])
      // inject plugin and context in the arguments of the exposed method.
      for (const methodName in this.pluginMethods) {
        const method = this.pluginMethods[methodName]
        this.pluginMethods[methodName] = (...args) => method.call(this.pluginMethods, ...args, this.plugin, this.context)
      }
      // initialize the connection between Iframe and the UI
      this.pluginIframeConnection = connectToChild({
        iframe: iframeEl,
        methods: this.combinedMethods
      })
      const info = this.getSlotInfo()
      const iframe = await this.pluginIframeConnection.promise
      iframe.contextUpdate(info.handler, this.context)
    },
    displayErrors (...errors) {
      for (const error of errors) {
        this.$message.error(error)
      }
    },
    displayMessage (message, type = 'success') {
      this.$message({
        message,
        type
      })
    },
    async displayInfoPopUp (message, title, options = {}) {
      try {
        await this.$confirm(message, title, {
          type: 'info',
          showCancelButton: false,
          maxWidth: '60em',
          ...options
        })
        return true
      } catch (e) {
        return false
      }
    },
    getAccessToken () {
      return this.accessToken
    },
    async runPendingExecutions () {
      // no-op
    }
  },
  mounted () {
    if (this.pluginSlot) {
      this.connectToPluginIframe()
    }
  }
}
